import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { 
  SidebarContainer, 
  SidebarCard 
} from '@components/ui/Sidebar';
import { Avatar, AvatarFallback } from '@components/ui/Avatar';
import { 
  RiNotification3Line,
  RiArrowRightSLine,
  RiLogoutBoxRLine,
} from '@remixicon/react';
import { TrustJetLogoColored, TrustJetLogoBlack } from '@assets/images';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/lib/supabase';
import { ROUTES, SIDEBAR_MENUS } from '@/constants';

const NavigationSidebar = ({
  currentPage,
  onNavigate,
  onShowNotifications,
  userProfile = {
    avatar: '/default-avatar.png',
    name: 'User',
    isVerified: false
  },
  onShowProfile,
}) => {

  const { logout, setAuthType, userProfile: authUserProfile } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  /**
   * Load messages (for bids inbox)
   */
  const loadMessages = useCallback(async () => {
    try {
      const data = await apiClient.getMessages(0, 5, { processed: false });
      setMessages(data);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadMessages()]);
      setLoading(false);
    };
    loadData();
  }, [loadMessages]);
  
  const handleNavClick = (pageId) => {
    if (onNavigate) {
      onNavigate(pageId);
    }
  };

  const handleLogout = async() => {
    logout();
    setAuthType('home');
    await supabase.auth.signOut();
    window.location.href = ROUTES.HOME.path;
  };
    
  const { getUser } = useAuthStore();
  const user = getUser();
  const userInitial = user?.email?.charAt(0).toUpperCase() || 'U';

  return (
    <SidebarContainer className="bg-transparent overflow-visible z-[30] group">
      
      {/* Navigation Items */}
      <SidebarCard className="flex-1 gap-2 border-0 !rounded-2xl py-5 px-2 overflow-visible w-16 group-hover:w-56 group-hover/nav:w-56 transition-all duration-300 ease-in-out group-hover:px-4 group-hover/nav:px-4">
        
        {/* Logo Section */}
        <div className="relative h-7 w-full overflow-hidden mb-3">
          <img src={TrustJetLogoColored} alt="Logo" className="h-7 w-auto object-contain group-hover:opacity-0 group-hover/nav:opacity-0 transition-opacity duration-300" />
          <img src={TrustJetLogoBlack} alt="Logo" className="h-7 w-auto object-contain absolute top-0 left-0 opacity-0 group-hover:opacity-100 group-hover/nav:opacity-100 transition-opacity duration-300" />
        </div>

        {SIDEBAR_MENUS.filter((item) => !item.roles || item.roles.includes(authUserProfile?.role)).map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.route;
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.route)}
              className={`
                relative w-full h-12 rounded-xl overflow-visible
                flex items-center justify-start gap-3 px-3
                transition-all duration-200 ease-in-out
                ${isActive
                  ? 'bg-white/30 text-slate-800'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                }
              `}
              aria-label={item.label}
              title={item.label}
            >
              {isActive && (
                <span className="absolute -left-2 group-hover:-left-4 group-hover/nav:-left-4 top-1/2 -translate-y-1/2 w-[4px] h-10 bg-primary-500 rounded-r-full transition-all duration-300" />
              )}
              <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-slate-700'}`} />
              <span className="opacity-0 group-hover:opacity-100 group-hover/nav:opacity-100 transition-opacity duration-300 whitespace-nowrap text-sm font-medium overflow-hidden flex-1 text-left">
                {item.label}
              </span>
              <RiArrowRightSLine className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover/nav:opacity-100 transition-opacity duration-300" />
            </button>
          );
        })}

        {/* Bottom Section */}
        <div className="mt-auto pt-4 flex flex-col gap-3 w-full">
          {/* Notifications Button */}
          <button
            onClick={onShowNotifications}
            className="
              relative w-full h-12 rounded-xl 
              flex items-center justify-start gap-3 px-3
              text-slate-600 hover:bg-slate-50 hover:text-slate-800
              transition-all duration-200 ease-in-out
            "
            aria-label="Notifications"
            title="Notifications"
          >
            <div className="relative flex-shrink-0">
              <RiNotification3Line className="w-5 h-5" strokeWidth={1.5} />
              {messages.length > 0 && (
                <span className="
                  absolute -top-1 -right-1 
                  min-w-[16px] h-[16px] px-1
                  bg-red-500 text-white text-[9px] font-semibold
                  rounded-full flex items-center justify-center
                  ring-2 ring-white
                ">
                  {messages.length > 9 ? '9+' : messages.length}
                </span>
              )}
            </div>
            <span className="opacity-0 group-hover:opacity-100 group-hover/nav:opacity-100 transition-opacity duration-300 whitespace-nowrap text-sm font-medium overflow-hidden flex-1 text-left">
              Notifications
            </span>
          </button>

          {/* User Profile Button */}
          <div className="relative w-full h-12 rounded-xl flex items-center px-3 hover:bg-slate-50 transition-all duration-200 ease-in-out group/profile">
            <button
              onClick={onShowProfile}
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
              aria-label="Profile"
              title={userProfile.name}
            >
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-indigo-100 text-indigo-700 font-semibold text-sm">
                  {userInitial}
                </AvatarFallback>
              </Avatar>
              
              {/* Verification Badge */}
              {userProfile.isVerified && (
                <div className="
                  absolute -bottom-0.5 -right-0.5 
                  w-4 h-4 bg-blue-500 rounded-full
                  flex items-center justify-center
                  ring-2 ring-white
                ">
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    className="w-2.5 h-2.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" 
                      fill="currentColor"
                    />
                  </svg>
                </div>
              )}
            </button>

            {/* User Name - visible on hover */}
            <span className="opacity-0 group-hover:opacity-100 group-hover/nav:opacity-100 transition-opacity duration-300 whitespace-nowrap text-sm font-medium overflow-hidden text-left text-slate-700 ml-3">
              {user?.email && user.email.length > 12 ? `${user.email.substring(0, 12)}...` : user?.email}
            </span>

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* Logout Icon - visible on hover */}
            <button
              onClick={handleLogout}
              className="opacity-0 group-hover:opacity-100 group-hover/nav:opacity-100 transition-opacity duration-300 flex-shrink-0 w-4 h-4 flex items-center justify-center hover:text-red-600 text-slate-600"
              aria-label="Logout"
              title="Logout"
            >
              <RiLogoutBoxRLine className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          
        </div>
      </SidebarCard>
    </SidebarContainer>
  );
};

export default NavigationSidebar;